import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";

// TikTok icon component
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  
  // Hide navbar on admin page
  if (location.pathname === "/admin") return null;

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Editorial", path: "/articles" },
    { name: "Achievements", path: "/achievements" },
    { name: "About", path: "/about" },
    { name: "Press", path: "/press" }
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-zinc-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60" data-testid="navbar">
      <div className="container">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="font-serif text-2xl lg:text-3xl font-bold tracking-tight"
            data-testid="logo-link"
          >
            FILMCABLE
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-sans text-sm font-bold uppercase tracking-widest transition-colors ${
                  isActive(link.path)
                    ? "text-[#FF66C4]"
                    : "text-zinc-600 hover:text-zinc-900"
                }`}
                data-testid={`nav-${link.name.toLowerCase()}-link`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Social Links - Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="https://instagram.com/filmcable"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-[#FF66C4] transition-colors"
              data-testid="social-instagram-link"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a>
            <a
              href="https://tiktok.com/@filmcable_"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-[#FF66C4] transition-colors"
              data-testid="social-tiktok-link"
              aria-label="TikTok"
            >
              <TikTokIcon size={18} />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-6 border-t border-zinc-200 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`font-sans text-lg font-bold uppercase tracking-widest ${
                    isActive(link.path)
                      ? "text-[#FF66C4]"
                      : "text-zinc-600"
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}-link`}
                >
                  {link.name}
                </Link>
              ))}
              
              <div className="flex items-center gap-6 pt-6 border-t border-zinc-200">
                <a
                  href="https://instagram.com/filmcable"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-[#FF66C4]"
                  aria-label="Instagram"
                >
                  <Instagram size={20} />
                </a>
                <a
                  href="https://tiktok.com/@filmcable_"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-[#FF66C4]"
                  aria-label="TikTok"
                >
                  <TikTokIcon size={20} />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
